// New-user onboarding (Phase 49): the constants both the frontend and the backend read, defined
// once so the trigger allowlist, the review-trigger split and the signup-source vocabulary can
// never drift between the auth modal, the signup parser and the onboarding scheduler.
//
// PURE. The only import is './gamification' (itself dependency-free), for FULL_REVIEW_POINTS.
//  - the BACKEND imports it through the compiled `shared` package entry;
//  - the FRONTEND imports the TS SOURCE through the `@shared/*` alias.
//
// Spec: .planning/phases/49-new-user-onboarding-first-7-days/49-CONTEXT.md (D-01, D-02, D-04,
// D-07, D-10, D-26) and 49-UI-SPEC.md (DD-12, DD-13).

import { POINT_VALUES } from './gamification';

// ─── Auth modal triggers ───────────────────────────────────────────────────────

// Mirrors the AuthModalTrigger union in frontend/src/contexts/AuthContext.tsx. The backend uses
// AUTH_MODAL_TRIGGERS as the allowlist when it parses the signup context, so an unknown value from
// the client is dropped instead of stored.
export const authModalTriggerMap = {
  nav: "nav",
  add_store: "add_store",
  contribute: "contribute",
  write_review: "write_review",
  comment: "comment",
  rate_link: "rate_link",
  zero_results: "zero_results",
  leaderboard: "leaderboard",
  messenger_link: "messenger_link",
  proof_card: "proof_card",
  claim: "claim",
  chat_limit: "chat_limit",
  moderator_view: "moderator_view",
  notifications: "notifications",
} as const;
export type AuthModalTrigger = keyof typeof authModalTriggerMap;
export const AUTH_MODAL_TRIGGERS = Object.keys(authModalTriggerMap) as AuthModalTrigger[];

// D-07: signups that came to write (or engage with) a review go straight back to that intent; the
// browse-style triggers get the welcome card. `claim` and `moderator_view` are in neither list:
// both have their own post-signup flow.
export const REVIEW_TRIGGERS = ["write_review", "rate_link", "contribute", "comment"] as const;
export type ReviewTrigger = (typeof REVIEW_TRIGGERS)[number];

export const WELCOME_CARD_TRIGGERS = [
  "nav",
  "leaderboard",
  "notifications",
  "chat_limit",
  "messenger_link",
  "proof_card",
  "zero_results",
  "add_store",
] as const;
export type WelcomeCardTrigger = (typeof WELCOME_CARD_TRIGGERS)[number];

// ─── Signup source (D-04) ──────────────────────────────────────────────────────

export const signupSourceMap = {
  email: "email",
  google: "google",
  facebook: "facebook",
  phone: "phone",
  whatsapp_flow: "whatsapp_flow",
} as const;
export type SignupSource = keyof typeof signupSourceMap;

// What the client tells the backend about the moment of signup. Stored on the user and read by the
// onboarding scheduler to pick the store a message is about.
export type SignupContext = {
  trigger: AuthModalTrigger;
  storeSlug?: string;
  lastViewedStoreSlug?: string;
};

// Per-user onboarding progress. Each *SentAt is written once, which is what makes every message
// at-most-once.
export type UserOnboardingState = {
  welcomeSentAt?: string | Date;
  day3SentAt?: string | Date;
  day7SentAt?: string | Date;
  returnedAt?: string | Date;
  returnedVia?: "session" | "link";
  welcomeCardDismissedAt?: string | Date;
};

// ─── Constants ─────────────────────────────────────────────────────────────────

// UI surfaces (welcome card, return banner) are gated on "account under 8 days old".
export const ONBOARDING_WINDOW_DAYS = 8;
// D-01 / D-26: a user counts as activated by a return visit between 24h and 7 days after signup.
export const ACTIVATION_MIN_HOURS = 24;
export const ACTIVATION_MAX_DAYS = 7;
// D-10: the welcome message waits an hour so it does not land on top of the signup itself.
export const WELCOME_DELAY_MINUTES = 60;
// UI-SPEC DD-12: return-link query params (signed token and message variant).
export const RETURN_TOKEN_PARAM = "ort";
export const RETURN_VARIANT_PARAM = "ov";

// Store slugs are generated in ecommerceScraper/dataTransformService.ts and
// agent/services/orchestrator.ts as `name.toLowerCase().replace(/[^\w\s-]/g, "")` plus dashes, so
// generated slugs are [a-z0-9_-] (`\w` keeps the underscore) with an optional `-<epoch>` suffix.
// Older and hand-edited slugs may carry Arabic letters or uppercase, hence the Arabic block and
// the `i` flag.
export const STORE_SLUG_RE = /^[a-z0-9_؀-ۿ-]{1,120}$/i;

// A complete review (rating, text, photo, factors). There is no single constant for it in
// gamification.ts; the fact ladder's rung 3 compares pointsToNext against this (UI-SPEC DD-13).
export const FULL_REVIEW_POINTS =
  POINT_VALUES.review_base + POINT_VALUES.review_text + POINT_VALUES.review_photo + POINT_VALUES.review_factors;

export type OnboardingNotificationType = "welcome" | "onboarding_day3" | "onboarding_day7";

export const isOnboardingType = (type: string): type is OnboardingNotificationType =>
  type === "welcome" || type === "onboarding_day3" || type === "onboarding_day7";
